import { Key, MoreVert, Person, ShoppingCart, TrendingUp } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, Container, Grid, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PRICING, ROUTES } from '@/config/app.config';
import { paymentsApi } from '@/infrastructure/services/api-client';
import { useApiKeysStore } from '@/stores/use-api-keys-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useThemeStore } from '@/stores/use-theme-store';

export function DashboardPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, refreshProfile } = useAuthStore();
    const { getActiveKeys, fetchKeys } = useApiKeysStore();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [portalLoading, setPortalLoading] = useState(false);

    const handleManageSubscription = async () => {
        setMenuAnchor(null);
        setPortalLoading(true);
        try {
            const { portalUrl } = await paymentsApi.createPortal();
            window.location.href = portalUrl;
        } catch {
            addNotification(t('purchase.manageSubscriptionError'), 'error');
        } finally {
            setPortalLoading(false);
        }
    };

    // Refresh data on mount and handle checkout redirect
    useEffect(() => {
        void fetchKeys();
        void refreshProfile();

        const checkout = searchParams.get('checkout');
        if (checkout === 'success') {
            addNotification(t('purchase.success'), 'success');
            // Clean the URL
            window.history.replaceState({}, '', ROUTES.dashboard);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) {
        return null;
    }

    const activeKeys = getActiveKeys();
    const activePlans = user.subscriptions;

    const statCards = [
        {
            title: t('dashboard.activePlans'),
            value: activePlans.length,
            icon: <TrendingUp sx={{ fontSize: 32 }} />,
            color: '#10B981',
        },
        {
            title: t('dashboard.activeApiKeys'),
            value: activeKeys.length,
            icon: <Key sx={{ fontSize: 32 }} />,
            color: '#F39323',
        },
    ];

    const quickActions = [
        { label: t('dashboard.generateKey'), icon: <Key />, action: () => navigate(ROUTES.apiKeys) },
        { label: t('dashboard.managePlans'), icon: <ShoppingCart />, action: () => navigate(ROUTES.purchase) },
        { label: t('dashboard.viewProfile'), icon: <Person />, action: () => navigate(ROUTES.profile) },
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {t('dashboard.welcome', { name: user.name.split(' ')[0] })}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {t('dashboard.title')}
            </Typography>

            {/* Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {statCards.map((stat) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={stat.title}>
                        <Card
                            sx={{
                                border: '1px solid',
                                borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            {stat.title}
                                        </Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                            {stat.value}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ color: stat.color, opacity: 0.8 }}>{stat.icon}</Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Active Plans */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t('dashboard.activePlans')}
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {activePlans.length > 0 ? (
                    activePlans.map((planId) => {
                        const plan = planId === 'mcp-abap' ? PRICING.plans.abap : PRICING.plans.btp;
                        const isAdminGranted = user.adminGrantedPlans?.includes(planId);
                        return (
                            <Grid size={{ xs: 12, sm: 6 }} key={planId}>
                                <Card
                                    sx={{
                                        border: '1px solid',
                                        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {plan.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {isAdminGranted ? (
                                                    <Chip label={t('dashboard.adminGranted')} color="info" size="small" variant="outlined" />
                                                ) : (
                                                    <>
                                                        <Chip label={t('purchase.active')} color="success" size="small" />
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                                                            disabled={portalLoading}
                                                        >
                                                            <MoreVert fontSize="small" />
                                                        </IconButton>
                                                        <Menu
                                                            anchorEl={menuAnchor}
                                                            open={Boolean(menuAnchor)}
                                                            onClose={() => setMenuAnchor(null)}
                                                        >
                                                            <MenuItem onClick={handleManageSubscription}>
                                                                {t('purchase.manageSubscription')}
                                                            </MenuItem>
                                                        </Menu>
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {t(plan.descriptionKey)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {plan.mcps.map((mcp) => (
                                                <Chip
                                                    key={mcp.name}
                                                    label={mcp.name}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            ))}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })
                ) : (
                    <Grid size={{ xs: 12 }}>
                        <Card sx={{ border: '1px dashed', borderColor: 'divider' }}>
                            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary" sx={{ mb: 2 }}>
                                    {t('dashboard.noPlans')}
                                </Typography>
                                <Button variant="contained" onClick={() => navigate(ROUTES.purchase)}>
                                    {t('dashboard.subscribePlan')}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>

            {/* Quick Actions */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t('dashboard.quickActions')}
            </Typography>
            <Grid container spacing={2}>
                {quickActions.map((action) => (
                    <Grid size={{ xs: 12, sm: 4 }} key={action.label}>
                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={action.icon}
                            onClick={action.action}
                            sx={{
                                py: 2,
                                borderRadius: 3,
                                justifyContent: 'flex-start',
                                fontWeight: 600,
                            }}
                        >
                            {action.label}
                        </Button>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
}

export default DashboardPage;
